import { useState, useCallback, useMemo } from 'react';
import { Calendar, Globe, Trophy } from 'lucide-react';
import useRailFilters from './useRailFilters';

/**
 * useVolleyballRailFilters - Volleyball-specific rail filter configuration
 * Provides tabs, filters, and handlers for Volleyball dashboard
 *
 * @param {Function} onFiltersChange - Callback when filters change
 * @param {string} initialDate - Initial date value (ISO string)
 */
const useVolleyballRailFilters = ({
  onFiltersChange,
  initialDate
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showCountrySheet, setShowCountrySheet] = useState(false);
  const [showLeagueSheet, setShowLeagueSheet] = useState(false);

  // Default filter values - captured once on mount
  const defaultFilters = useMemo(() => {
    return {
      date: initialDate || new Date().toISOString().split('T')[0],
      country: '',
      league: ''
    };
  }, []); // Empty deps - capture initial values only once!

  // Use base rail filters hook
  const {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    buildFilterObjects
  } = useRailFilters({
    sport: 'volleyball',
    defaultFilters,
    onFiltersChange
  });

  // Format date for display
  const formatDateLabel = useCallback((dateStr) => {
    if (!dateStr) return 'Select Date';

    // Handle if dateStr is actually a Date object
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const day = String(dateStr.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }

    // Validate the date string format
    if (typeof dateStr !== 'string' || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.warn('Invalid date format:', dateStr);
      return 'Select Date';
    }

    // Parse the date string correctly to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);

    const today = new Date();
    today.setHours(12, 0, 0, 0);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);

    // Compare dates properly
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);

    if (dateCompare.getTime() === today.getTime()) {
      return 'Today';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      const monthNum = date.getMonth() + 1;
      const dayNum = date.getDate();
      return `${dayName} ${monthNum}/${dayNum}`;
    }
  }, []);

  // Format country for display
  const formatCountryLabel = useCallback((country) => {
    if (!country || country === '') return 'All Countries';
    return country;
  }, []);

  // Format league for display
  const formatLeagueLabel = useCallback((league) => {
    if (!league || league === '') return 'All Leagues';
    // If league is an object with name property
    if (typeof league === 'object' && league.name) {
      return league.name;
    }
    return league;
  }, []);

  // Primary tabs configuration - Volleyball keeps it simple with just "All Games"
  const primaryTabs = useMemo(() => {
    return [
      { id: 'all', label: 'All Games' }
    ];
  }, []);

  // Secondary filter configuration
  const filterConfig = useMemo(() => {
    return [
      {
        id: 'date',
        type: 'date',
        getLabel: formatDateLabel,
        hasDropdown: true,
        isActive: filters.date !== defaultFilters.date
      },
      {
        id: 'country',
        type: 'select',
        getLabel: formatCountryLabel,
        hasDropdown: true,
        isActive: filters.country !== defaultFilters.country && filters.country !== ''
      },
      {
        id: 'league',
        type: 'select',
        getLabel: formatLeagueLabel,
        hasDropdown: true,
        isActive: filters.league !== defaultFilters.league && filters.league !== ''
      }
    ];
  }, [formatDateLabel, formatCountryLabel, formatLeagueLabel, filters.date, filters.country, filters.league, defaultFilters]);

  // Build filter objects for SecondaryRail
  const filterObjects = buildFilterObjects(filterConfig);

  // Handle filter chip click
  const handleFilterClick = useCallback((filterId) => {
    switch (filterId) {
      case 'date':
        setShowDateSheet(true);
        break;
      case 'country':
        setShowCountrySheet(true);
        break;
      case 'league':
        setShowLeagueSheet(true);
        break;
      default:
        break;
    }
  }, []);

  // Handle primary tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    // Volleyball only has "all" tab, so no additional filter updates needed
  }, []);

  return {
    // State
    filters,
    activeTab,
    hasActiveFilters,

    // Tabs and filters
    primaryTabs,
    filterObjects,

    // Handlers
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,

    // Sheet visibility
    showDateSheet,
    setShowDateSheet,
    showCountrySheet,
    setShowCountrySheet,
    showLeagueSheet,
    setShowLeagueSheet
  };
};

export default useVolleyballRailFilters;
